import React, { useEffect, useState } from "react";
import { Box, Card, CircularProgress, Divider, TableBody, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Paginator from "../../../toolkit/Paginator";
import Restoreicon from "../../../icons/Restoreicon.svg";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import axios from "../../../utils/fvAxios";
import BaseUrl from "../../../utils/base-uri.json";
import { isArray } from "lodash";
import _ from "lodash";
import LoadingSpinner from "../../../toolkit/LoadingSpinner";
import ErrorAlert from "../../../toolkit/ErrorAlert";
import { FormatToLocalTimeString } from "../../../utils/dateFormat";
import GetfVDateFormate from "../../../utils/Genaricfunctions";
import RightArrowOpen from "../../../icons/RightArrowOpen.svg";
import { KeyboardArrowDown, KeyboardArrowUp, UnfoldMore, } from "@mui/icons-material";
import debounce from 'lodash/debounce';

const queryUrl = BaseUrl.baseUrl + BaseUrl.OnsiteOffsite.GetOnsiteEmployees;
const pageSize = 10;

const toQueryString = (obj) => {
  return "?".concat(
    Object.keys(obj)
      .map((e) => {
        if (isArray(obj[e])) {
          var d = obj[e]?.map(
            (r, idx) => `${e}[${idx}]=${encodeURIComponent(r)}`
          );
          return d.join("&");
        }
        return obj[e]
          ? `${encodeURIComponent(e)}=${encodeURIComponent(obj[e])}`
          : null;
      })
      .filter((e) => !!e)
      .join("&")
  );
};

const getUrl = (url, data) => `${url}${toQueryString(data)}`;


const headers = [
  { label: "Vendor Name", key: "vendorName" },
  { label: "Company Name", key: "company" },
  { label: "Business Unit", key: "buName" },
  { label: "Site Name", key: "siteName" },
  { label: "Employee/Visitor Name", key: "visitorName" },
  { label: "Site Contact", key: "siteContact" },
  { label: "Security Name Checkin", key: "securityNameCheckin" },
  { label: "Last Checkin", key: "employeeCheckin" },
];

const convertToCSV = (data) => {
  const header = headers.map((header) => `"${header.label}"`).join(",");
  const rows = data.map((item) =>
    headers
      .map((header) => {
        let value = item[header.key];

        // Check if the value is null or undefined and return an empty string
        if (value === null || value === undefined) {
          return '""';
        }
        return `"${value}"`;
      })
      .join(",")
  );
  return [header, ...rows].join("\n");
};


export default function CurrentlyOnsite({ businessUnitId, locationId }) {
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.action.hover,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
      border: 0,
      marginBottom: "20px",
    },
  }));

  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [Searchdebounce, setSearchdebounce] = useState("");
  const [refresh, setRefresh] = useState(true);
  const [isDownload, setIsDownload] = useState(false);
  const [onsiteEmployees, setOnsiteEmployees] = useState(null);
  const [orderBy, setOrderBy] = useState({
    sortingColumn: "VendorName",
    sortAscending: true,
  });
  const [permissions, setPermissions] = useState({
    hasTagPermission: false,
    hasBadgePermission: false,
  });

  const [paramsState, setParamsState] = useState({
    location: locationId,
    pageNumber: pageNumber,
    searchValue: searchValue,
    pageSize: pageSize,
    orderBy: orderBy.sortingColumn,
    sortOrder: orderBy.sortAscending ? "ASC" : "DESC",
  });

  useEffect(() => {
    if (refresh) {
      if (searchValue === Searchdebounce) {
        setLoading(true);
        const url = getUrl(queryUrl, { ...paramsState });

        axios
          .get(url)
          .then((res) => {
            if (res?.data?.responseMessage === "Success") {
              const data = res?.data?.data?.checkinList;
              setPageCount(Math.ceil((res?.data?.count || pageSize) / pageSize));
              setOnsiteEmployees(data);
              setPermissions({
                hasTagPermission: res?.data?.data?.hasPermission,
                hasBadgePermission: res?.data?.data?.hasBadgePermission,
              });
            } else {
              setError(res?.data?.responseMessage);
            }
          })
          .catch((err) => setError(err))
          .finally(() => {
            setLoading(false);
            setRefresh(false);
          });
      }
    }
  }, [paramsState, refresh, searchValue, Searchdebounce]);

  useEffect(() => {
    setParamsState((prev) => ({
      ...prev,
      location: locationId,
      pageNumber: 1,
    }));
    setRefresh(true);
  }, [locationId]);

  // const debouncedSearch = _.debounce((searchValue) => {
  //   setParamsState((prev) => ({ ...prev, searchValue, pageNumber: 1 }));
  //   setRefresh(true);
  // }, 1000);

  // const changeSearchValue = (value) => {
  //   if (searchValue !== value) {
  //     setSearchValue(value);
  //     debouncedSearch(value);
  //   }
  // };

  const delayedSearch = debounce((value) => {
    setSearchdebounce(value)
    setParamsState((prev) => ({
      ...prev,
      search: value,
      pageNumber: 1,
    }));
    setRefresh(true);
  }, 2000);

  const changeSearchValue = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    delayedSearch(value);
  };

  const setPage = (pageNumber) => {
    if (pageNumber > 0) {
      setPageNumber(pageNumber);
      setParamsState((prev) => ({ ...prev, pageNumber }));
      setRefresh(true);
    }
  };

  const checkoutEmployee = (onsiteOffsiteID, vendorEmployeeID, vendorID) => {
    const data = {
      onsiteOffsiteID: onsiteOffsiteID,
      vendorEmployeeID: vendorEmployeeID,
      vendorID: vendorID,
      clientBusinessUnitID: businessUnitId,
      clientBusinessUnitSiteID: locationId,
    };
    axios
      .post(BaseUrl.baseUrl + BaseUrl.OnsiteOffsite.CheckoutEmployee, data)
      .then((resp) => {
        if (resp?.data?.data === "Success") {
          setRefresh(true);
        }
      }).catch((err) => setError(err));
  };

  const resetTagorBadgeNumber = (
    onsiteOffsiteID,
    employeeId,
    isTagNumber,
    isVisitor
  ) => {
    const data = {
      onsiteOffsiteID,
      employeeId,
      isTagNumber,
      isVisitor,
    };
    axios
      .post(BaseUrl.baseUrl + BaseUrl.OnsiteOffsite.ResetTagOrBadgeNumber, data)
      .then((resp) => {
        if (resp?.data?.data === "Success") {
          setRefresh(true);
        }
      }).catch((err) => setError(err));
  };

  const checkoutVisitor = (onsiteOffsiteID, vendorEmployeeID) => {
    const data = {
      onsiteOffsiteID: onsiteOffsiteID,
      vendorEmployeeID: vendorEmployeeID,
      clientBusinessUnitID: businessUnitId,
      clientBusinessUnitSiteID: locationId,
    };
    axios
      .post(BaseUrl.baseUrl + BaseUrl.OnsiteOffsite.CheckoutVisitor, data)
      .then((resp) => {
        if (resp?.data?.data === "Success") {
          setRefresh(true);
        }
      }).catch((err) => setError(err));
  };

  const resetEmployee = (onsiteOffsiteID) => {
    axios
      .post(
        BaseUrl.baseUrl + BaseUrl.OnsiteOffsite.ResetEmployee + onsiteOffsiteID,
        {}
      )
      .then((resp) => {
        if (resp?.data?.data === "Success") {
          setRefresh(true);
        }
      }).catch((err) => setError(err));
  };

  const resetVisitor = (onsiteOffsiteID) => {
    axios
      .post(
        BaseUrl.baseUrl + BaseUrl.OnsiteOffsite.ResetVisitor + onsiteOffsiteID,
        {}
      )
      .then((resp) => {
        if (resp?.data?.data === "Success") {
          setRefresh(true);
        }
      }).catch((err) => setError(err));
  };

  const sortTable = (sortingColumn) => {
    if (orderBy.sortingColumn === sortingColumn) {
      setOrderBy((prev) => ({
        ...prev,
        sortAscending: !orderBy.sortAscending,
      }));
    } else {
      setOrderBy({
        sortingColumn,
        sortAscending: true,
      });
    }
  };

  useEffect(() => {
    setParamsState((prev) => ({
      ...prev,
      orderBy: orderBy.sortingColumn,
      sortOrder: orderBy.sortAscending ? "ASC" : "DESC",
    }));
    setRefresh(true);
  }, [orderBy]);

  const downloadCurrentlyOnsite = () => {
    setIsDownload(true);
    const timeZoneOffset = new Date().getTimezoneOffset()
    const downloadURL = BaseUrl.baseUrl + BaseUrl.OnsiteOffsite.CurrentlyOnsite;
    const url = getUrl(downloadURL, { location: locationId, timeZoneOffset: timeZoneOffset,DownloadClientData:true});
    axios.get(url).then((res) => {
      if (res?.data?.status === true) {
        if (res?.data?.data) {
          const csvContent = convertToCSV(res?.data?.data);
          const blob = new Blob([csvContent], { type: "text/csv" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "CurrentlyOnsite.csv";
          a.click();
          URL.revokeObjectURL(url);
        }
      }
    })
      .catch((err) => setError(err))
      .finally(() => {
        setIsDownload(false);
      });
  }
  return (
    <>
      <Card>
        <div style={{ marginLeft: "10px", padding: "10px", color: "red" }}>
          This tab is for listing all vendor employees and visitors currently
          onsite. Download a spreadsheet of the names by clicking the Download
          button. To check an <br></br>employee or visitor out, click the
          Offsite radio button (the name will disappear from the list).
        </div>
        <Divider
          sx={{
            marginTop: 2,
            marginBottom: 2,
            backgroundColor: "#667085;",
            height: "2px",
          }}
        />
        <div style={{ marginLeft: "15px", marginBottom: "15px" }}>
          <Stack spacing={2} direction="row">
            <TextField
              placeholder="Search"
              value={searchValue}
              onChange={changeSearchValue}
              sx={{
                "& legend": { display: "none" },
                "& fieldset": { top: 0 },
                fontFamily: "Arial, FontAwesome",
              }}
              variant="outlined"
              size="small"
              hidden={true}
            />
            <div style={{ marginLeft: "Auto", padding: "10px" }}>
              {!isDownload ? (<Button variant="contained" sx={{ padding: "inherit" }}
                onClick={downloadCurrentlyOnsite}>
                Download
              </Button>)
                : (<CircularProgress size="1rem" />)
              }
            </div>
          </Stack>
        </div>
        <Box sx={{ backgroundColor: "#f8f9fa" }}>
          {loading ? (
            <LoadingSpinner />
          ) : onsiteEmployees?.length > 0 ? (
            <TableContainer component={Paper} sx={{ borderBottom: 0 }}>
              <Table aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell onClick={() => sortTable("VendorName")}>
                      <span style={{ color: "#667085", fontSize: "16px" }}>
                        Vendor Name
                      </span>
                      <IconButton>
                        {orderBy.sortingColumn === "VendorName" ? (
                          orderBy.sortAscending ? (
                            <KeyboardArrowUp />
                          ) : (
                            <KeyboardArrowDown />
                          )
                        ) : (
                          <UnfoldMore />
                        )}
                      </IconButton>
                    </StyledTableCell>
                    <StyledTableCell onClick={() => sortTable("CompanyName")}>
                      <span style={{ color: "#667085", fontSize: "16px" }}>
                        Company Name
                      </span>
                      <IconButton>
                        {orderBy.sortingColumn === "CompanyName" ? (
                          orderBy.sortAscending ? (
                            <KeyboardArrowUp />
                          ) : (
                            <KeyboardArrowDown />
                          )
                        ) : (
                          <UnfoldMore />
                        )}
                      </IconButton>
                    </StyledTableCell>
                    <StyledTableCell onClick={() => sortTable("VisitorName")}>
                      <span style={{ color: "#667085", fontSize: "16px" }}>
                        Employee/Visitor Name
                      </span>
                      <IconButton>
                        {orderBy.sortingColumn === "VisitorName" ? (
                          orderBy.sortAscending ? (
                            <KeyboardArrowUp />
                          ) : (
                            <KeyboardArrowDown />
                          )
                        ) : (
                          <UnfoldMore />
                        )}
                      </IconButton>
                    </StyledTableCell>
                    {permissions?.hasTagPermission && (
                      <StyledTableCell onClick={() => sortTable("TagNumber")}>
                        <span style={{ color: "#667085", fontSize: "16px" }}>
                          Tag #
                        </span>
                        <IconButton>
                          {orderBy.sortingColumn === "TagNumber" ? (
                            orderBy.sortAscending ? (
                              <KeyboardArrowUp />
                            ) : (
                              <KeyboardArrowDown />
                            )
                          ) : (
                            <UnfoldMore />
                          )}
                        </IconButton>
                      </StyledTableCell>
                    )}
                    {permissions?.hasBadgePermission && (
                      <StyledTableCell onClick={() => sortTable("BadgeNumber")}>
                        <span style={{ color: "#667085", fontSize: "16px" }}>
                          Badge #
                        </span>
                        <IconButton>
                          {orderBy.sortingColumn === "BadgeNumber" ? (
                            orderBy.sortAscending ? (
                              <KeyboardArrowUp />
                            ) : (
                              <KeyboardArrowDown />
                            )
                          ) : (
                            <UnfoldMore />
                          )}
                        </IconButton>
                      </StyledTableCell>
                    )}
                    <StyledTableCell onClick={() => sortTable("Checkin")}>
                      <span style={{ color: "#667085", fontSize: "16px" }}>
                        Check-In
                      </span>
                      <IconButton>
                        {orderBy.sortingColumn === "Checkin" ? (
                          orderBy.sortAscending ? (
                            <KeyboardArrowUp />
                          ) : (
                            <KeyboardArrowDown />
                          )
                        ) : (
                          <UnfoldMore />
                        )}
                      </IconButton>
                    </StyledTableCell>
                    <StyledTableCell>
                      <span style={{ color: "#667085", fontSize: "16px" }}>
                        Offsite
                      </span>
                    </StyledTableCell>
                  </TableRow>
                </TableHead>

                {onsiteEmployees?.map((r, i) => (
                  <TableBody key={i}>
                    <TableRow key={i}>
                      <StyledTableCell sx={{ width: "12%" }}>
                        {r.vendorName}
                      </StyledTableCell>

                      <StyledTableCell sx={{ width: "12%" }}>
                        <span>{r.companyName}</span>
                      </StyledTableCell>

                      <StyledTableCell sx={{ width: "16%" }}>
                        <span>{r.visitorName}</span>
                      </StyledTableCell>

                      {permissions?.hasTagPermission && (
                        <StyledTableCell sx={{ width: "12%" }}>
                          <span>
                            {
                              r.tagNumber &&
                              <Tooltip title="Reset Tag">
                                <IconButton
                                  aria-label="Restoreicon"
                                  onClick={() =>
                                    resetTagorBadgeNumber(
                                      r.onsiteOffsiteID,
                                      r.employeeOrVisitorID,
                                      true,
                                      r.vendorName ? false : true
                                    )
                                  }
                                >
                                  <img src={Restoreicon} alt="Restoreicon" />
                                </IconButton>
                              </Tooltip>
                            }
                            <span style={{ fontSize: "initial" }}>
                              {r.tagNumber}
                            </span>
                          </span>
                        </StyledTableCell>
                      )}

                      {permissions?.hasBadgePermission && (
                        <StyledTableCell sx={{ width: "15%" }}>
                          <span>
                            {r.badgeNumber &&
                              <Tooltip title="Reset Badge Number">
                                <IconButton
                                  aria-label="Restoreicon1"
                                  onClick={() =>
                                    resetTagorBadgeNumber(
                                      r.onsiteOffsiteID,
                                      r.employeeOrVisitorID,
                                      false,
                                      r.vendorName ? false : true
                                    )
                                  }
                                >
                                  <img src={Restoreicon} alt="Restoreicon1" />
                                </IconButton>
                              </Tooltip>
                            }
                            <span style={{ fontSize: "initial" }}>
                              {r.badgeNumber}
                            </span>
                          </span>
                        </StyledTableCell>
                      )}

                      <StyledTableCell sx={{ width: "20%" }}>
                        <span>
                          <Tooltip title="Reset Check-In">
                            <IconButton
                              aria-label="Restoreicon2"
                              onClick={() =>
                                r.vendorName
                                  ? resetEmployee(r.onsiteOffsiteID)
                                  : resetVisitor(r.onsiteOffsiteID)
                              }
                            >
                              <img src={Restoreicon} alt="Restoreicon2" />
                            </IconButton>
                          </Tooltip>
                          <span style={{ fontSize: "initial" }}>
                            {GetfVDateFormate("", "", r.checkin)}
                          </span>
                        </span>
                      </StyledTableCell>

                      <StyledTableCell sx={{ width: "15%" }}>
                        <Tooltip title="Offsite">
                          <IconButton
                            aria-label="OpenArrow"
                            onClick={() =>
                              r.vendorName
                                ? checkoutEmployee(
                                  r.onsiteOffsiteID,
                                  r.employeeOrVisitorID,
                                  r.vendorID
                                )
                                : checkoutVisitor(
                                  r.onsiteOffsiteID,
                                  r.employeeOrVisitorID
                                )
                            }
                          >
                            <span>
                              <img src={RightArrowOpen} alt="OpenArrow" />{" "}
                              <span
                                style={{
                                  color: "#2A93DB",
                                  fontSize: "initial",
                                }}
                              >
                                Offsite
                              </span>
                            </span>
                          </IconButton>
                        </Tooltip>
                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                ))}
              </Table>
            </TableContainer>
          ) : error ? (
            <ErrorAlert message={error} />
          ) : (
            <ErrorAlert severity="warning" message={"No results found!"} />
          )}
        </Box>
      </Card>

      {!loading && onsiteEmployees?.length > 0 && (
        <Paginator
          count={pageCount}
          page={Number(pageNumber)}
          margin="18px 0px"
          onChange={setPage}
        />
      )}
    </>
  );
}
